import * as XLSX from 'xlsx'
import Quiz from './models/quiz'
import QuizAnswer from './models/quiz-answer'

/**
 * Import de quizz « Le saviez-vous » depuis un fichier Excel.
 *
 * Une ligne par quizz, une seule feuille (la premiere). Les colonnes sont
 * reperees par leur en-tete, pas par leur position :
 *
 *   jour                      date Excel ou texte AAAA-MM-JJ (ou JJ/MM/AAAA)
 *   theme_<langue>            obligatoire en fr et en
 *   question_<langue>         obligatoire en fr et en
 *   explication_<langue>      facultative
 *   reponse1_<langue> … reponse4_<langue>
 *                             2 a 4 reponses, fr et en pour chacune
 *   bonne_reponse             le numero (1 a 4) de la bonne reponse
 *   publie                    oui / non, vide = non
 *
 * Ce module ne parle pas a l'utilisateur : il rend des codes d'erreur
 * (`{ code, params }`), traduits par le composant d'import.
 */

export const LANGUAGES = ['fr', 'en', 'it', 'de', 'es', 'ja', 'ko', 'zh', 'nl']
export const REQUIRED_LANGUAGES = ['fr', 'en']
export const MAX_ANSWERS = 4

const TRANSLATED = ['theme', 'question', 'explication']
const ANSWER = /^reponse([1-9][0-9]*)$/

/** Les en-tetes du modele : fr et en seulement, le reste est facultatif. */
export const TEMPLATE_HEADERS = [
  'jour',
  ...['theme', 'question', 'explication'].flatMap(field => REQUIRED_LANGUAGES.map(l => `${field}_${l}`)),
  ...Array.from({ length: MAX_ANSWERS }, (_, i) => REQUIRED_LANGUAGES.map(l => `reponse${i + 1}_${l}`)).flat(),
  'bonne_reponse',
  'publie'
]

const TEMPLATE_EXAMPLE = {
  jour: '2026-11-03',
  theme_fr: 'Cépages',
  theme_en: 'Grapes',
  question_fr: 'Quel cépage domine à Pomerol ?',
  question_en: 'Which grape dominates in Pomerol?',
  explication_fr: 'Le merlot aime les sols argileux de Pomerol, où il mûrit tôt.',
  explication_en: 'Merlot thrives on the clay soils of Pomerol, where it ripens early.',
  reponse1_fr: 'Cabernet sauvignon',
  reponse1_en: 'Cabernet sauvignon',
  reponse2_fr: 'Merlot',
  reponse2_en: 'Merlot',
  reponse3_fr: 'Syrah',
  reponse3_en: 'Syrah',
  bonne_reponse: 2,
  publie: 'non'
}

/** Le modele a telecharger : les en-tetes et une ligne d'exemple. */
export function downloadTemplate () {
  const sheet = XLSX.utils.aoa_to_sheet([
    TEMPLATE_HEADERS,
    TEMPLATE_HEADERS.map(header => TEMPLATE_EXAMPLE[header] === undefined ? '' : TEMPLATE_EXAMPLE[header])
  ])
  sheet['!cols'] = TEMPLATE_HEADERS.map(header => ({ wch: header === 'jour' ? 12 : header.startsWith('reponse') ? 22 : 30 }))
  const book = XLSX.utils.book_new()
  XLSX.utils.book_append_sheet(book, sheet, 'Quiz')
  XLSX.writeFile(book, 'modele-import-quiz.xlsx')
}

/** « Réponse 1 FR » → « reponse1_fr » : sans accents, espaces ni majuscules. */
function normalizeHeader (value) {
  return String(value || '')
    .normalize('NFD').replace(/[̀-ͯ]/g, '')
    .trim().toLowerCase()
    .replace(/[\s-]+/g, '_')
}

function text (value) {
  return value === null || value === undefined ? '' : String(value).trim()
}

/**
 * Le jour, en « AAAA-MM-JJ ». Une cellule date d'Excel arrive en numero de
 * serie : `parse_date_code` le convertit sans passer par un fuseau, sinon un
 * navigateur a l'ouest de Greenwich decalerait le quizz a la veille.
 */
function parseDay (value) {
  if (typeof value === 'number') {
    const date = XLSX.SSF.parse_date_code(value)
    return date ? isoDay(date.y, date.m, date.d) : null
  }
  const raw = text(value)
  let match = raw.match(/^(\d{4})-(\d{1,2})-(\d{1,2})$/)
  if (match) return isoDay(+match[1], +match[2], +match[3])
  match = raw.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/)
  if (match) return isoDay(+match[3], +match[2], +match[1])
  return null
}

function isoDay (year, month, day) {
  const date = new Date(Date.UTC(year, month - 1, day))
  // Rejette le 31/02 : Date le ferait glisser au 3 mars sans rien dire.
  if (date.getUTCFullYear() !== year || date.getUTCMonth() !== month - 1 || date.getUTCDate() !== day) return null
  return date.toISOString().slice(0, 10)
}

const YES = ['oui', 'yes', 'true', 'vrai', '1', 'x']
const NO = ['non', 'no', 'false', 'faux', '0', '']

/**
 * Lit le fichier et valide chaque ligne pour elle-meme et contre les autres
 * lignes du fichier. Le controle contre les quizz deja planifies vient a part
 * (`checkExistingDays`) : il demande l'API.
 *
 * Rend `{ errors, rows }` : `errors` porte les erreurs du fichier entier (pas
 * de feuille, colonne inconnue…), qui bloquent tout l'import ; chaque ligne
 * porte les siennes.
 */
export function parseWorkbook (data) {
  const book = XLSX.read(data, { type: 'array' })
  const sheet = book.Sheets[book.SheetNames[0]]
  if (!sheet) return { errors: [{ code: 'emptyFile' }], rows: [] }

  const [headerRow = [], ...lines] = XLSX.utils.sheet_to_json(sheet, { header: 1, raw: true, defval: '' })
  const headers = headerRow.map(normalizeHeader)

  const errors = []
  const unknown = headerRow.filter((_, index) => headers[index] && !isKnownHeader(headers[index]))
  if (unknown.length) errors.push({ code: 'unknownColumns', params: { columns: unknown.join(', ') } })
  const duplicated = headers.filter((header, index) => header && headers.indexOf(header) !== index)
  if (duplicated.length) errors.push({ code: 'duplicateColumns', params: { columns: [...new Set(duplicated)].join(', ') } })
  if (!headers.includes('jour')) errors.push({ code: 'missingColumn', params: { column: 'jour' } })
  if (!headers.includes('bonne_reponse')) errors.push({ code: 'missingColumn', params: { column: 'bonne_reponse' } })
  for (const field of ['theme', 'question']) {
    for (const language of REQUIRED_LANGUAGES) {
      if (!headers.includes(`${field}_${language}`)) errors.push({ code: 'missingColumn', params: { column: `${field}_${language}` } })
    }
  }

  const rows = []
  lines.forEach((cells, index) => {
    if (cells.every(cell => text(cell) === '')) return
    const values = {}
    headers.forEach((header, column) => { if (header) values[header] = cells[column] })
    // + 2 : la ligne d'en-tete, et Excel compte a partir de 1.
    rows.push(parseRow(values, index + 2))
  })

  if (!errors.length && !rows.length) errors.push({ code: 'noRows' })
  flagDuplicateDays(rows)
  return { errors, rows }
}

function isKnownHeader (header) {
  if (header === 'jour' || header === 'bonne_reponse' || header === 'publie') return true
  const [field, language, ...rest] = header.split('_')
  if (rest.length || !LANGUAGES.includes(language)) return false
  if (TRANSLATED.includes(field)) return true
  const answer = field.match(ANSWER)
  return !!answer && +answer[1] <= MAX_ANSWERS
}

function translations (values, field) {
  const result = {}
  for (const language of LANGUAGES) {
    const value = text(values[`${field}_${language}`])
    if (value) result[language] = value
  }
  return result
}

function missing (translation) {
  return REQUIRED_LANGUAGES.filter(language => !translation[language])
}

function parseRow (values, line) {
  const errors = []

  const day = parseDay(values.jour)
  if (!text(values.jour)) errors.push({ code: 'dayMissing' })
  else if (!day) errors.push({ code: 'dayInvalid', params: { value: text(values.jour) } })

  const theme = translations(values, 'theme')
  const question = translations(values, 'question')
  const explanation = translations(values, 'explication')
  for (const [field, value] of [['theme', theme], ['question', question]]) {
    const languages = missing(value)
    if (languages.length) errors.push({ code: 'translationMissing', params: { field, languages: languages.join(', ').toUpperCase() } })
  }

  // Les reponses gardent le numero de leur colonne : c'est lui que vise
  // `bonne_reponse`, meme si une colonne du milieu est restee vide.
  const answers = []
  for (let number = 1; number <= MAX_ANSWERS; number++) {
    const label = translations(values, `reponse${number}`)
    if (!Object.keys(label).length) continue
    const languages = missing(label)
    if (languages.length) errors.push({ code: 'answerTranslationMissing', params: { number, languages: languages.join(', ').toUpperCase() } })
    answers.push({ number, label })
  }
  if (answers.length < 2) errors.push({ code: 'twoAnswers' })

  const rawCorrect = text(values.bonne_reponse)
  const correct = /^\d+$/.test(rawCorrect) ? +rawCorrect : null
  if (!rawCorrect) errors.push({ code: 'correctMissing' })
  else if (!correct || correct < 1 || correct > MAX_ANSWERS) errors.push({ code: 'correctInvalid', params: { value: rawCorrect, max: MAX_ANSWERS } })
  else if (!answers.some(answer => answer.number === correct)) errors.push({ code: 'correctEmpty', params: { number: correct } })

  const rawPublished = normalizeHeader(values.publie)
  if (!YES.includes(rawPublished) && !NO.includes(rawPublished)) {
    errors.push({ code: 'publishedInvalid', params: { value: text(values.publie) } })
  }

  return {
    line,
    day,
    theme,
    question,
    explanation,
    answers: answers.map(answer => ({ label: answer.label, correct: answer.number === correct })),
    published: YES.includes(rawPublished),
    errors
  }
}

/** Deux lignes sur le meme jour : aucune des deux ne passe, on ne choisit pas. */
function flagDuplicateDays (rows) {
  const byDay = {}
  for (const row of rows) {
    if (row.day) (byDay[row.day] = byDay[row.day] || []).push(row)
  }
  for (const group of Object.values(byDay)) {
    if (group.length < 2) continue
    for (const row of group) {
      const others = group.filter(other => other !== row).map(other => other.line).join(', ')
      row.errors.push({ code: 'dayDuplicate', params: { lines: others } })
    }
  }
}

/** La question d'un quizz existant, dans la langue de l'utilisateur. */
function questionOf (entry, locale) {
  const question = entry.question || {}
  return question[locale] || question.en || question.fr || '—'
}

/**
 * Les ressources d'une reponse lue avec `save: false`. Ce n'est PAS le
 * document JSON:API brut : le `transformResponse` du store le passe deja par
 * `dataNormalizer.documentToModel`, qui rend des objets a plat (un tableau
 * pour une liste, un objet pour une ressource seule).
 */
function resources (response) {
  const data = response.response.data
  if (!data) return []
  return Array.isArray(data) ? data : [data]
}

/**
 * Marque en erreur les lignes dont le jour a deja un quizz. Une requete par
 * jour, comme le controle du formulaire : CRUD-Wine refuserait de toute facon
 * un jour pris, ceci ne sert qu'a le dire avant d'ecrire.
 */
export async function checkExistingDays (rows, { baseURL, locale }) {
  for (const row of rows) {
    if (!row.day) continue
    const response = await Quiz.api().get(`quizzes?lang=${locale}`, {
      baseURL,
      save: false,
      params: { filter: `day==${row.day}T00:00:00Z`, 'page[size]': 1 }
    })
    const found = resources(response)[0]
    if (found) row.errors.push({ code: 'dayTaken', params: { question: questionOf(found, locale) } })
  }
}

/**
 * Cree un quizz et ses reponses, comme le formulaire a la creation : le quizz,
 * puis les reponses une a une (la base refuse deux bonnes reponses), puis la
 * publication en second geste (CRUD-Wine force `published = false` a la
 * creation). Un echec en route laisse un quizz non publie, donc invisible de
 * l'application.
 */
export async function createQuiz (row, { baseURL, locale }) {
  const quiz = await Quiz.new()
  Object.assign(quiz, {
    day: `${row.day}T00:00:00Z`,
    theme: row.theme,
    question: row.question,
    explanation: row.explanation,
    answers: [],
    published: false
  })
  quiz.$isNew = true
  const response = await Quiz.api().post(`quizzes?lang=${locale}`, quiz, { baseURL, save: false })
  const quizId = resources(response)[0].id

  for (const [index, answer] of row.answers.entries()) {
    const draft = await QuizAnswer.new()
    Object.assign(draft, { label: answer.label, correct: answer.correct, position: index + 1 })
    draft.quiz_id = quizId
    // `$entity_name` donne son type JSON:API au lien : sans lui, 400 cote creddy.
    draft.quiz = { $entity_name: Quiz.entity, id: quizId }
    draft.$isNew = true
    await QuizAnswer.api().post('quiz-answers', draft, { baseURL, save: false })
  }

  if (row.published) {
    await Quiz.api().patch(
      `quizzes/${quizId}`,
      { $entity_name: Quiz.entity, id: quizId, published: true },
      { baseURL, save: false }
    )
  }
  return quizId
}
